import React from 'react';
import {
  Container,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '8px !important',
  backgroundColor: '#1a1a1a',
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    backgroundColor: '#1a1a1a',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#2a2a2a',
    },
  },
  '& .MuiAccordionDetails-root': {
    backgroundColor: '#1a1a1a',
    borderRadius: '0 0 8px 8px',
  },
  '& .MuiTypography-root': {
    color: '#ffffff',
  },
  '& .MuiSvgIcon-root': {
    color: '#ffffff',
  },
}));

// Add a styled wrapper for the entire page
const PageWrapper = styled('div')({
  backgroundColor: '#000000',
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const FAQAndTerms = () => {
  const faqs = [
    {
      question: 'How do I book a journey?',
      answer: 'Bookings can be made directly through our booking link, via WhatsApp (0739 113 7185), or by calling us on 0203 488 0235.',
    },
    {
      question: 'Is a deposit required?',
      answer: 'Yes. A £50 deposit is required to secure all bookings. This is deducted from the final journey total.',
    },
    {
      question: 'How do I pay the deposit?',
      answer: 'Deposit payments can be made via bank transfer. Details will be provided during the booking confirmation process.',
    },
    {
      question: 'When do I pay the rest of my balance?',
      answer: 'The remaining balance must be paid before the journey begins, unless otherwise agreed in writing.',
    },
    {
      question: 'What if I\'m running late?',
      answer: 'We offer a 15-minute grace period. After that, a late fee may apply. Please let us know as early as possible if you expect a delay.',
    },
    {
      question: 'Can I add stops during my journey?',
      answer: 'Yes — if the stops fall within your booked duration, they are included. Additional time beyond your scheduled booking will incur extra charges.',
    },
    {
      question: 'What\'s included in the journey?',
      answer: 'Our V-Class vehicles come equipped with:\n\n• 42" TV\n• Premium sound system\n• Privacy screen\n• Ambient lighting\n• Royal blue luxury seating\n• Optional extras like our Celebrations Package',
    },
    {
      question: 'What areas do you cover?',
      answer: 'We serve London, the Midlands, and the North of England — including Manchester, Birmingham, Coventry, and surrounding areas.',
    },
    {
      question: 'Can I request a specific vehicle?',
      answer: 'Yes — just let us know your preference during booking, and we\'ll confirm availability.',
    },
    {
      question: 'Is my deposit refundable?',
      answer: 'Deposits are non-refundable, but bookings cancelled more than 48 hours in advance can be rescheduled once free of charge, subject to availability.',
    },
  ];

  return (
    <PageWrapper>
      <Container 
        maxWidth="md" 
        sx={{ 
          py: 8,
          color: '#ffffff',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              color: '#ffffff',
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            FAQ & Terms
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Frequently Asked Questions and Terms of Service
          </Typography>
        </Box>

        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              color: '#ffffff',
              mb: 4,
              fontWeight: 'bold',
            }}
          >
            Frequently Asked Questions
          </Typography>
          {faqs.map((faq, index) => (
            <StyledAccordion key={index}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    margin: '12px 0',
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  sx={{
                    whiteSpace: 'pre-line',
                    color: 'rgba(255, 255, 255, 0.7)',
                  }}
                >
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </StyledAccordion>
          ))}
        </Box>

        <Box>
          <Typography
            variant="h4"
            sx={{
              color: '#ffffff',
              mb: 4,
              fontWeight: 'bold',
            }}
          >
            Terms & Conditions
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              whiteSpace: 'pre-line',
              mb: 4,
            }}
          >
            {`1. Booking and Payment
• All bookings require a £50 deposit to secure the reservation.
• The remaining balance must be paid before the journey begins.
• Payment methods include bank transfer and cash (by prior arrangement).

2. Cancellation Policy
• Deposits are non-refundable.
• Cancellations made more than 48 hours before the journey can be rescheduled once.
• Cancellations within 48 hours of the journey will forfeit the deposit.

3. Vehicle Usage
• Smoking is strictly prohibited in all vehicles.
• Food and drink are allowed but must be consumed responsibly.
• Any damage to the vehicle will be charged to the client.

4. Journey Details
• The quoted price includes the agreed route and duration.
• Additional stops or extended duration will incur extra charges.
• Waiting time beyond the grace period will be charged.

5. Client Responsibilities
• Clients must provide accurate pickup and drop-off locations.
• Clients are responsible for any tolls or parking fees.
• Clients must ensure all passengers comply with safety regulations.

6. Company Responsibilities
• We will provide a clean, well-maintained vehicle.
• We will ensure professional and courteous service.
• We will maintain appropriate insurance coverage.

7. Force Majeure
• We are not liable for delays or cancellations due to circumstances beyond our control.
• This includes but is not limited to: severe weather, road closures, or vehicle breakdowns.

8. Privacy Policy
• We respect your privacy and will not share your information with third parties.
• We may use your contact information for booking-related communications.

9. Complaints
• Any complaints should be made in writing within 24 hours of the journey.
• We will investigate and respond to all complaints within 5 working days.

10. Governing Law
• These terms and conditions are governed by English law.
• Any disputes will be subject to the exclusive jurisdiction of the English courts.`}
          </Typography>
        </Box>
      </Container>
    </PageWrapper>
  );
};

export default FAQAndTerms;